import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SectionLabel } from "../components/label";
import { Modal } from "../components/modal";
import {
  Button,
  FormErrors,
  InputContainer,
  InputNumber,
  InputSelect,
  InputSwitch,
  InputText,
  Label,
  Option,
  useForm,
} from "../components/form";
import { Dollar, Location, Trash } from "../icons";
import { ILoan, propertyTypeOptionsData, usageTypeData } from ".";
import { ISecurity } from "./Loan";
import { ILiability } from "../liabilities";
import { Alert } from "../components/alert";
import { toFixed } from "../util";
import LoansForm from "./LoansForm";
import { FormikProps } from "formik";
import useStore from "../store";

type Values = Omit<ILoan, "_id" | "LoanId" | "Security"> &
  Omit<ISecurity, "PropertyType"> & {
    PropertyType: ISecurity["PropertyType"][0];
    PropertySubType: ISecurity["PropertyType"][1];
    PropertySubSubType: ISecurity["PropertyType"][2];
    TotalTermYears?: number;
    InterestOnlyTermYears?: number;
    SecurityType: ISecurity["SecurityType"];
    Liability?: Partial<Omit<ILiability, "_id">> & {
      InterestOnlyTermYears?: number | null;
      TotalTermYears?: number | null;
    };
  };

type SecurityFormModalProps = {
  open: boolean;
  title: string;
  initialValue?: Partial<SecurityOutputValues>;
  mode?: "add" | "edit";
  handleClose?: () => void;
  onDelete?: () => void;
  onSave?: (value: Partial<SecurityOutputValues>) => void;
  SecurityId?: ISecurity["SecurityId"];
};

type SecurityOutputValues = {
  Security: ISecurity;
  Loans: Array<
    Omit<ILoan, "_id"> & {
      Liability?: Partial<
        Omit<ILiability, "ApplicantId" | "LiabilityType"> & {
          LoanId: Nullable<number>;
        }
      >;
    }
  >;
  Liability?: Array<
    Partial<
      Omit<ILiability, "_id" | "ApplicantId" | "LiabilityType"> & {
        LoanId: number;
      }
    >
  >;
};

type SecurityFormValues = Omit<ISecurity, "PropertyType"> & {
  PropertyType: ISecurity["PropertyType"][0];
  PropertySubType: ISecurity["PropertyType"][1];
  PropertySubSubType: ISecurity["PropertyType"][2];
  TotalTermYears?: number;
  SecurityType: ISecurity["SecurityType"];
};

const defaultValues = {
  Value: NaN,
  UsageType: null,
  PropertyType: null,
  PropertySubType: null,
  PropertySubSubType: null,
  SecurityType: "Proposed",
  Location: null,
  IsRBNZExempt: false,
  Lender: null,
  LoanType: "New",
  TopUpAmount: NaN,
  LendingAction: null,
  LoanAmount: NaN,
  TotalTermYears: NaN,
  TotalTermMonths: 12 * 30,
  InterestOnlyTermYears: NaN,
  InterestOnlyTermMonths: NaN,
  LiabilityType: "HomeLoan",
  Liability: {
    LiabilityType: "HomeLoan",
    RepaymentAmount: NaN,
    RepaymentFrequency: "Monthly",
    Limit: null,
    Balance: null,
    InterestRate: null,
    RepaymentType: null,
    StartDate: null,
    TotalTermYears: NaN,
    TotalTermMonths: NaN,
    InterestOnlyTermYears: NaN,
    InterestOnlyTermMonths: NaN,
    Lender: null,
    IsRevolvingCredit: null,
  },
} as const;

export default function SecurityFormModal({
  open,
  title,
  initialValue,
  handleClose,
  onSave,
  mode = "add",
  onDelete,
}: SecurityFormModalProps) {
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoansValid, setIsLoansValid] = useState<boolean>(true);

  const [
    ApplicantParty,
    LoanSecurityMapping,
    deleteLoanItem,
    deleteMappingItem,
    deleteFinancialItem,
  ] = useStore(
    useCallback(
      (state) => [
        Array.from(state.ApplicantParties.values())[0],
        Array.from(state.LoanSecurityMapping.values()),
        state.deleteLoanItem,
        state.deleteMappingItem,
        state.deleteFinancialItem,
      ],
      []
    )
  );

  const Loans = (initialValue?.Loans || []).map((_loan) => {
    return {
      LoanId: _loan?.LoanId,
      Lender: _loan?.Lender ?? defaultValues.Lender,
      LoanType: _loan?.LoanType ?? defaultValues.LoanType,
      TopUpAmount: _loan?.TopUpAmount ?? defaultValues.TopUpAmount,
      LendingAction: _loan?.LendingAction ?? defaultValues.LendingAction,
      LoanAmount: _loan?.LoanAmount ?? defaultValues.LoanAmount,
      TotalTermYears: Math.floor(
        (_loan?.TotalTermMonths || defaultValues.TotalTermMonths) / 12
      ),
      TotalTermMonths:
        (_loan?.TotalTermMonths || defaultValues.TotalTermMonths) % 12,
      InterestOnlyTermYears: Math.floor(
        (_loan?.InterestOnlyTermMonths ?? 0) / 12
      ),
      InterestOnlyTermMonths: (_loan?.InterestOnlyTermMonths ?? 0) % 12,
      LiabilityType: defaultValues.LiabilityType,
      Liability: {
        ...defaultValues.Liability,
        ..._loan.Liability,
        InterestRate: _loan.Liability?.InterestRate
          ? toFixed(_loan.Liability.InterestRate * 100)
          : undefined,
        TotalTermYears: Math.floor(
          (_loan.Liability?.TotalTermMonths || defaultValues.TotalTermMonths) /
            12
        ),
        TotalTermMonths:
          (_loan.Liability?.TotalTermMonths || defaultValues.TotalTermMonths) %
          12,
        InterestOnlyTermYears: Math.floor(
          (_loan.Liability?.InterestOnlyTermMonths ?? 0) / 12
        ),
        InterestOnlyTermMonths:
          (_loan.Liability?.InterestOnlyTermMonths ?? 0) % 12,
      },
    };
  });

  const { handleChange, values, resetValues, hasErrors } =
    useForm<SecurityFormValues>({
      initialValues: {
        SecurityId: initialValue?.Security?.SecurityId ?? 1,
        Value: initialValue?.Security?.Value ?? defaultValues.Value,
        UsageType: initialValue?.Security?.UsageType ?? defaultValues.UsageType,
        PropertyType:
          initialValue?.Security?.PropertyType[0] ?? defaultValues.PropertyType,
        PropertySubType:
          initialValue?.Security?.PropertyType[1] ??
          defaultValues.PropertySubSubType,
        PropertySubSubType:
          initialValue?.Security?.PropertyType[2] ??
          defaultValues.PropertySubSubType,
        SecurityType:
          initialValue?.Security?.SecurityType ?? defaultValues.SecurityType,
        Location: initialValue?.Security?.Location ?? defaultValues.Location,
        IsRBNZExempt:
          initialValue?.Security?.IsRBNZExempt ?? defaultValues.IsRBNZExempt,
      },
      validate: (values) => {
        const errors: FormErrors<Values> = {};

        if (!values.SecurityType) {
          errors.SecurityType = "Security type can't be empty";
        }

        if (!values.Value) {
          errors.Value = "Security Value can't be empty or zero";
        }

        return errors;
      },
      validateOnMount: true,
    });

  const loansFormRef = useRef<FormikProps<any>>(null);

  useEffect(() => {
    if (!open) {
      resetValues();
    }
  }, [open, resetValues]);

  const propertyTypeOptions = propertyTypeOptionsData.filter(
    ({ sub_type_of }) => sub_type_of === 0
  );
  const propertySubTypeOptions = useMemo(() => {
    const selected = propertyTypeOptionsData.find(
      (type) => type.code === values.PropertyType
    );

    const propertySubTypeOptions = propertyTypeOptionsData.filter(
      (type) => type.sub_type_of === selected?.id
    );

    if (
      !propertySubTypeOptions.some(
        (type) => type.code === values.PropertySubType
      )
    ) {
      handleChange("PropertySubType", null);
    }

    return propertySubTypeOptions;
    // ADDING values.PropertySubType CAUSING INFINITE RERENDER
    // WE DON'T NEED THAT
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.PropertyType]);

  const propertySubSubTypeOptions = useMemo(() => {
    const selected = propertyTypeOptionsData.find(
      (type) => type.code === values.PropertySubType
    );
    const propertySubSubTypeOptions = propertyTypeOptionsData.filter(
      (type) => type.sub_type_of === selected?.id
    );

    if (
      !propertySubSubTypeOptions.some(
        (type) => type.code === values.PropertySubSubType
      )
    ) {
      handleChange("PropertySubSubType", null);
    }

    return propertySubSubTypeOptions;
    // ADDING values.PropertySubType CAUSING INFINITE RERENDER
    // WE DON'T NEED THAT
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.PropertySubType]);

  function isEmpty(obj: any) {
    for (var prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        return false;
      }
    }

    return true;
  }

  function handleValidation(values: any) {
    const errors: FormErrors<any> = {};

    if (!values.LoanType) {
      errors.LoanType = "Loan Type can't be empty";
    }

    if (values.LoanType === "New") {
      if (!values.LoanAmount) {
        errors.LoanAmount = "Loan Amount can't be empty or zero";
      }
      if (!values.TotalTermMonths && !values.TotalTermYears) {
        errors.TotalTermMonths = "Loan Term can't be empty or zero";
      }
    } else if (values.LoanType === "Existing") {
      if (!values.LendingAction) {
        errors.LendingAction = "Lending Action can't be empty";
      }

      if (values.LendingAction === "None") {
        if (!values.Liability?.RepaymentAmount) {
          errors.Liability = "Repayments can't be empty or zero";
        }
        if (!values.Liability?.Limit) {
          errors.LoanAmount = "Loan Amount can't be empty or zero";
        }
        if (!values.Liability?.Balance) {
          errors.Liability = "Balance can't be empty or zero";
        }
        if (!values.Liability?.InterestRate) {
          errors.Liability = "Interest rate can't be empty or zero";
        }
        if (
          !values.Liability?.TotalTermMonths &&
          !values.Liability?.TotalTermYears
        ) {
          errors.TotalTermMonths = "Loan Term can't be empty or zero";
        }
      } else if (values.LendingAction === "Refinance") {
        if (!values.LoanAmount) {
          errors.LoanAmount = "Loan Amount can't be empty or zero";
        }
        if (!values?.TotalTermMonths && !values?.TotalTermYears) {
          errors.TotalTermMonths = "New Loan Term can't be empty or zero";
        }
      } else if (values.LendingAction === "TopUp") {
        if (!values.LoanAmount && !values.Liability?.Limit) {
          errors.LoanAmount = "Original Loan Amount can't be empty or zero";
        }
        if (!values.TopUpAmount) {
          errors.TopUpAmount = "Proposed Increaase can't be empty or zero";
        }
        if (
          !values.Liability?.TotalTermMonths &&
          !values.Liability?.TotalTermYears
        ) {
          errors.TotalTermMonths = "Original Loan Term can't be empty or zero";
        }
        if (!values.TotalTermMonths && !values.TotalTermYears) {
          errors.TotalTermMonths = "New Loan Term can't be empty or zero";
        }
        if (values.Lender) {
          if (!values.Liability?.InterestRate) {
            errors.Liability = "Current Interest Rate can't be empty or zero";
          }
          if (!values.Liability?.RepaymentAmount) {
            errors.Liability = " Current Repayments can't be empty or zero";
          }
          if (!values.Liability?.Balance) {
            errors.Liability = "Current Balance Rate can't be empty or zero";
          }
        }
      }
    }

    return errors;
  }

  function handleSave() {
    if (!onSave || !loansFormRef.current) return;
    const NewLoans = loansFormRef.current.values?.Loans;

    if (mode === "edit") {
      (initialValue?.Loans || [])?.forEach((_loan) => {
        if (!NewLoans?.find((_i: any) => _i.LoanId === _loan.LoanId)?.LoanId) {
          deleteLoanItem(_loan.LoanId);

          if (_loan.Liability?._id) {
            deleteFinancialItem(ApplicantParty._id, {
              type: "Liability",
              id: _loan.Liability._id,
            });
          }

          const MappingId = LoanSecurityMapping?.find(
            (_map) => _map.LoanId === _loan?.LoanId
          )?._id;
          if (MappingId) deleteMappingItem(MappingId);
        }
      });
    }

    const cleanData = cleanUpData({
      ...values,
      Loans: NewLoans,
    });
    onSave(cleanData);
  }

  return (
    <Modal containerClassName="max-w-[981px]" open={open}>
      <section>
        <header>
          <SectionLabel className="text-[15px] text-[#7B5C8D] desktop:text-lg">
            {title}
          </SectionLabel>
        </header>
        <main className="mt-[23px]">
          <div className="grid grid-cols-2 items-stretch gap-[20px]">
            <form className="h-full space-y-3 bg-[#F5F1F8] px-[15px] py-[20px]">
              <p className="mb-4 text-[15px] font-bold text-[#736878]">
                Security Details
              </p>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="" className="required">
                  Security Type
                </Label>
                <InputSwitch
                  value={values.SecurityType}
                  onChange={(value) => handleChange("SecurityType", value)}
                  className="min-w-[242px]"
                >
                  <InputSwitch.InputSwitchOption value="Proposed">
                    PROPOSED
                  </InputSwitch.InputSwitchOption>
                  <InputSwitch.InputSwitchOption value="Existing">
                    EXISTING
                  </InputSwitch.InputSwitchOption>
                </InputSwitch>
              </InputContainer>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="Value" className="required">
                  Value
                </Label>
                <InputNumber
                  id="Value"
                  buttons={false}
                  value={values.Value}
                  onChange={(value) => handleChange("Value", value)}
                  withIcon={<Dollar />}
                  className="min-w-[242px]"
                  autonumeric
                />
              </InputContainer>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="UsageType">Usage</Label>
                <InputSelect
                  className="min-w-[242px]"
                  initialValue={values.UsageType}
                  onChange={(value) => handleChange("UsageType", value)}
                >
                  <Option value={null} />
                  {usageTypeData.map((usage) => (
                    <Option value={usage.code} key={usage.id}>
                      {usage.name}
                    </Option>
                  ))}
                </InputSelect>
              </InputContainer>
              <InputContainer className="items-start gap-0 border-transparent py-0">
                <Label htmlFor="propertyType" className="mt-1">
                  Property Type
                </Label>
                <div className="min-w-[242px] space-y-3">
                  <InputSelect
                    initialValue={values.PropertyType}
                    onChange={(value) => handleChange("PropertyType", value)}
                  >
                    <Option value={null} />
                    {propertyTypeOptions.map((type) => (
                      <Option value={type.code} key={type.id}>
                        {type.type}
                      </Option>
                    ))}
                  </InputSelect>
                  {propertySubTypeOptions.length > 0 && (
                    <InputSelect
                      onChange={(value) =>
                        handleChange("PropertySubType", value)
                      }
                      initialValue={values.PropertySubType}
                    >
                      <Option value={null}></Option>
                      {propertySubTypeOptions.map((type) => (
                        <Option value={type.code} key={type.id}>
                          {type.type}
                        </Option>
                      ))}
                    </InputSelect>
                  )}
                  {propertySubSubTypeOptions.length > 0 && (
                    <InputSelect
                      onChange={(value) =>
                        handleChange("PropertySubSubType", value)
                      }
                      initialValue={values.PropertySubSubType}
                    >
                      <Option value={null}></Option>
                      {propertySubSubTypeOptions.map((type) => (
                        <Option value={type.code} key={type.id}>
                          {type.type}
                        </Option>
                      ))}
                    </InputSelect>
                  )}
                </div>
              </InputContainer>
              <InputContainer className="hidden gap-0 border-transparent py-0">
                <Label htmlFor="Location">Location</Label>
                <InputText
                  id="Location"
                  withIcon={<Location />}
                  className="min-w-[242px]"
                  value={values.Location ?? ""}
                  onChange={(e) => handleChange("Location", e.target.value)}
                />
              </InputContainer>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="IsRBNZExempt">RBNZ Exempt</Label>
                <InputSwitch
                  id="IsRBNZExempt"
                  value={values.IsRBNZExempt}
                  onChange={(value) => handleChange("IsRBNZExempt", value)}
                  className="min-w-[242px]"
                >
                  <InputSwitch.InputSwitchOption value={true}>
                    Yes
                  </InputSwitch.InputSwitchOption>
                  <InputSwitch.InputSwitchOption value={false}>
                    No
                  </InputSwitch.InputSwitchOption>
                </InputSwitch>
              </InputContainer>
            </form>
            <LoansForm
              innerRef={loansFormRef}
              initialValues={{
                Loans,
              }}
              onEditing={setIsEditing}
              onChange={(values) => {
                const loans = values?.Loans || [];
                if (loans?.length === 0) {
                  setIsLoansValid(false);
                  return;
                }

                const isValid = (loans || [])?.reduce(
                  (acc: boolean, loan: any, idx: number) => {
                    if (acc) {
                      const errors = handleValidation(loan);
                      acc = isEmpty(errors);
                    }
                    return acc;
                  },
                  true
                );

                setIsLoansValid(isValid);
              }}
              onSubmit={() => {}}
            />
          </div>
          <div className="mt-[21px] flex items-center justify-center gap-4">
            {mode === "edit" && (
              <Alert
                below={
                  <Button
                    type="button"
                    size="large"
                    className="w-[173px] border border-[#EF8B85] bg-[#EF8B85] disabled:border-[#C2B6CB]"
                    onClick={() => setDeleting(true)}
                    withIcon={
                      <Trash
                        fill="#fff"
                        className="w-[12px] desktop:w-[14px]"
                      />
                    }
                    disabled={hasErrors}
                  >
                    Delete Security
                  </Button>
                }
                open={deleting}
                backgroundColor="#1E1642"
                wrapperClassName="mr-auto"
                className="translate-y-1.5 text-left font-helvetica text-[11px] text-white shadow-none desktop:text-sm"
                arrowClassName="-translate-y-1 -translate-x-1"
                onClose={() => setDeleting(false)}
              >
                <p className="font-bold">Are you sure?</p>
                <p className="mt-[5px]">Deleting this item cannot be undone.</p>
                <div className="mt-3 flex items-center justify-end space-x-[28px]">
                  <Button
                    type="button"
                    size="small"
                    className="w-max bg-transparent text-white"
                    onClick={() => setDeleting(false)}
                  >
                    <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                      Cancel
                    </span>
                  </Button>
                  <Button
                    type="button"
                    className="w-max bg-[#E13756] px-4 py-2 font-bold text-white"
                    size="small"
                    onClick={() => {
                      onDelete && onDelete();
                      setDeleting(false);
                      handleClose && handleClose();
                    }}
                  >
                    <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                      Yes, delete
                    </span>
                  </Button>
                </div>
              </Alert>
            )}
            <Button
              type="button"
              size="large"
              className="w-[173px] border border-secondary_accent bg-transparent text-secondary_accent"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="large"
              className="w-[173px] border border-secondary_accent disabled:border-[#C2B6CB]"
              onClick={handleSave}
              disabled={hasErrors || !isLoansValid || isEditing}
            >
              Save and Close
            </Button>
          </div>
        </main>
      </section>
    </Modal>
  );
}

function cleanUpData(values: SecurityFormValues & { Loans: any[] }) {
  const data: SecurityOutputValues = {
    Security: {
      SecurityId: values.SecurityId,
      Value: values.Value,
      IsRBNZExempt: values.IsRBNZExempt,
      Location: values.Location,
      PropertyType: [
        values.PropertyType,
        values.PropertySubType,
        values.PropertySubSubType,
      ],
      SecurityType: values.SecurityType,
      UsageType: values.UsageType,
    },
    Loans: (values?.Loans || []).map((Loan) => {
      const loanData: any = {
        LoanId: Loan.LoanId,
        LoanType: Loan.LoanType,
        Lender: Loan.Lender ?? defaultValues.Lender,
        LendingAction: Loan.LendingAction ?? defaultValues.LendingAction,
        LiabilityType: Loan.LiabilityType ?? defaultValues.LiabilityType,
        LoanAmount: Loan.LoanAmount ?? defaultValues.LoanAmount,
        TopUpAmount: Loan.TopUpAmount ?? defaultValues.TopUpAmount,
        TotalTermMonths: Loan.TotalTermMonths ?? defaultValues.TotalTermMonths,
        InterestOnlyTermMonths:
          Loan.InterestOnlyTermMonths ?? defaultValues.InterestOnlyTermMonths,
      };

      if (Loan.LoanType === "New") {
        loanData.LoanAmount = Loan.LoanAmount;
        loanData.TotalTermMonths =
          (Loan.TotalTermYears ?? 0) * 12 + (Loan.TotalTermMonths ?? 0);
        loanData.InterestOnlyTermMonths =
          (Loan.InterestOnlyTermYears ?? 0) * 12 +
          (Loan.InterestOnlyTermMonths ?? 0);
      } else if (Loan.LoanType === "Existing" && Loan.Liability) {
        loanData.LendingAction = Loan.LendingAction;
        loanData.Liability = {
          ...defaultValues.Liability,
          ...Loan.Liability,
          InterestRate: Loan.Liability.InterestRate
            ? toFixed(Loan.Liability.InterestRate / 100)
            : null,
          TotalTermMonths:
            (Loan.Liability.TotalTermYears ?? 0) * 12 +
            (Loan.Liability.TotalTermMonths ?? 0),
          InterestOnlyTermMonths:
            (Loan.Liability.InterestOnlyTermYears ?? 0) * 12 +
            (Loan.Liability.InterestOnlyTermMonths ?? 0),
        };

        switch (Loan.LendingAction) {
          case "Refinance":
            loanData.LoanAmount = Loan.LoanAmount;
            loanData.TotalTermMonths =
              (Loan.TotalTermYears ?? 0) * 12 + (Loan.TotalTermMonths ?? 0);
            loanData.InterestOnlyTermMonths =
              (Loan.InterestOnlyTermYears ?? 0) * 12 +
              (Loan.InterestOnlyTermMonths ?? 0);
            loanData.Lender = Loan.Lender;
            break;
          case "TopUp":
            loanData.LoanAmount = Loan.LoanAmount;
            loanData.TopUpAmount = Loan.TopUpAmount;
            loanData.TotalTermMonths =
              (Loan.TotalTermYears ?? 0) * 12 + (Loan.TotalTermMonths ?? 0);
            loanData.InterestOnlyTermMonths =
              (Loan.InterestOnlyTermYears ?? 0) * 12 +
              (Loan.InterestOnlyTermMonths ?? 0);
            loanData.Lender = Loan.Lender;
            break;
        }
      }

      return loanData;
    }),
    Liability: [],
  };

  return data;
}
